import { ComponentProps } from "react"
import { Href, Link } from "expo-router"
import { ActivityIndicator, Platform, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import MIcon from "@react-native-vector-icons/material-design-icons"
import * as Burnt from "burnt"

import { ar } from "@src/i18n/ar"
import { useNumCols } from "@src/hooks"
import { downloadFile, sharedStyles, trpcClient } from "@src/utils"
import { Text } from "@src/components/core"
import { List } from "@src/components/list"
import { Header } from "@src/components/Header"
import { useMutation } from "@tanstack/react-query"

type IconName = ComponentProps<typeof MIcon>["name"]

type LinkItem = {
  type: "link"
  title: string
  icon: IconName
  href: Href
  bgClassName: string
}

type ActionItem = {
  type: "scaleSheet"
  title: string
  icon: IconName
  bgClassName: string
}

type Item = LinkItem | ActionItem

const content: Item[] = [
  { type: "link", title: ar.adjustments, icon: "clipboard-edit", href: "/dashboard/inventory/adjustments", bgClassName: "bg-[#A855F7]" },
  { type: "link", title: ar.images, icon: "image-album", href: "/dashboard/more/images", bgClassName: "bg-[#F59E0B]" },
  { type: "scaleSheet", title: ar.scaleSheet, icon: "scale-balance", bgClassName: "bg-[#10B981]" },
  { type: "link", title: ar.deletedProducts, icon: "delete", href: "/dashboard/more/deleted-products", bgClassName: "bg-[#EF4444]" },
  { type: "link", title: ar.config, icon: "cog", href: "/dashboard/more/config", bgClassName: "bg-[#6366F1]" },
  { type: "link", title: ar.expenseCategories, icon: "tag-multiple", href: "/dashboard/more/expense-categories" as Href, bgClassName: "bg-[#8B5CF6]" },
]

function ScaleSheetTile({ item }: { item: ActionItem }) {
  const download = useMutation({
    mutationFn: async () => {
      const data = await trpcClient.products.downloadScaleUpcCsv.query()
      const ok = downloadFile(data.csv, data.filename)
      Burnt.toast({ title: ok ? ar.downloadScaleSheetSuccess(data.count) : ar.downloadAvailableOnWeb, preset: ok ? "done" : "error" })
    },
  })

  return (
    <Pressable
      className={`${item.bgClassName}  py-8 px-4 rounded mx-1 mb-2 shadow-md items-center justify-center active:opacity-90 hover:shadow-xl hover:scale-105 transition-all min-h-[140]`}
      onPress={() => download.mutate()}
      disabled={download.isPending || Platform.OS !== "web"}
    >
      <View className="h-9 items-center justify-center">
        {download.isPending ? <ActivityIndicator color="white" size="large" /> : <MIcon name={item.icon} size={36} color="white" />}
      </View>
      <Text className="text-white font-bold text-center mt-3 text-base leading-tight" numberOfLines={2}>
        {item.title}
      </Text>
    </Pressable>
  )
}

export default function More() {
  const numCols = Math.max(useNumCols(190, { maxWidth: 1200 }), 2)

  return (
    <>
      <Header title={ar.more} className="mb-2" />
      <List
        data={content}
        contentContainerStyle={sharedStyles.contentContainer}
        numColumns={numCols}
        key={`dashboard-grid-${numCols}`}
        renderItem={({ item }) => {
          if (item.type === "scaleSheet") {
            return <ScaleSheetTile item={item} />
          }

          return (
            <Link href={item.href} asChild>
              <Pressable
                className={`${item.bgClassName} py-8 px-4 rounded mx-1 mb-2 shadow-md items-center justify-center active:opacity-90 hover:shadow-xl hover:scale-105 transition-all min-h-[140]`}
              >
                <View className="h-9">
                  <MIcon name={item.icon} size={36} color="white" />
                </View>
                <Text className="text-white font-bold text-center mt-3 text-base leading-tight" numberOfLines={2}>
                  {item.title}
                </Text>
              </Pressable>
            </Link>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />
    </>
  )
}
